import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, X } from 'lucide-react';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void | Promise<void>;
  variant?: 'danger' | 'warning';
  requireTypedConfirmation?: boolean;
  confirmationKeyword?: string;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  variant = 'warning',
  requireTypedConfirmation = false,
  confirmationKeyword = 'DELETE',
}) => {
  const [typedText, setTypedText] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const handleConfirm = async () => {
    if (requireTypedConfirmation && typedText !== confirmationKeyword) {
      return;
    }
    
    setLoading(true);
    try {
      await onConfirm();
      onOpenChange(false);
      setTypedText('');
    } catch (error) {
      console.error('Confirmation action failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setTypedText('');
  };

  const isConfirmDisabled = requireTypedConfirmation ? typedText !== confirmationKeyword : false;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000] animate-in fade-in duration-200" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 z-[1001] animate-in zoom-in-95 fade-in duration-200">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${variant === 'danger' ? 'bg-red-100' : 'bg-orange-100'}`}>
                <AlertTriangle size={24} className={variant === 'danger' ? 'text-red-600' : 'text-orange-600'} />
              </div>
              <Dialog.Title className="text-xl font-black text-gray-900">
                {title}
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button className="text-gray-400 hover:text-gray-600 transition-colors">
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>
          
          <Dialog.Description className="text-gray-600 text-sm mb-6">
            {message}
          </Dialog.Description>

          {requireTypedConfirmation && (
            <div className="mb-6">
              <p className="text-sm text-gray-700 mb-2">
                Type <strong className="text-red-600">{confirmationKeyword}</strong> to confirm:
              </p>
              <input
                type="text"
                value={typedText}
                onChange={(e) => setTypedText(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none font-bold"
                placeholder={`Type ${confirmationKeyword}`}
                autoFocus
              />
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              disabled={loading}
              className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-300 transition-colors disabled:opacity-50"
            >
              {cancelText}
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading || isConfirmDisabled}
              className={`flex-1 px-4 py-3 text-white rounded-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                variant === 'danger' 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-orange-600 hover:bg-orange-700'
              }`}
            >
              {loading ? 'Processing...' : confirmText}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
